import { Component, HostListener } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { registerI18n } from './core/i18n';
import { getCurrentPreferredLanguage, getDefaultLanguage } from './core/language-preference';
import { applyPreferredTheme } from './core/ui-preferences';
import { captureDevToolsFlag } from './gameplay/dev-test-access';
import { ClickSoundService } from './core/click-sound.service';
import { clickSoundTarget } from './core/click-sound-target';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent {
  constructor(
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly clickSound: ClickSoundService,
  ) {
    registerI18n(this.translateService);
    const defaultLanguage = getDefaultLanguage();
    this.translateService.setDefaultLang(defaultLanguage);
    const preferredLanguage = getCurrentPreferredLanguage();
    void this.translateService.use(preferredLanguage);
    // Keep <html lang> in sync with the active UI language (es-ES -> "es",
    // en-US -> "en") so the declared language always matches the rendered
    // content. Combined with translate="no" / the notranslate meta in
    // index.html, this stops browsers from auto-translating user content such
    // as names. Updates whenever the user switches language in Settings.
    this.syncDocumentLanguage(preferredLanguage);
    this.translateService.onLangChange.subscribe((event) =>
      this.syncDocumentLanguage(event.lang),
    );
    applyPreferredTheme();

    // Capture the `?devtools=1|0` opt-in from the initial URL and every
    // navigation. The gameplay launcher strips query params, so we persist the
    // flag rather than reading it on the gameplay route. See dev-test-access.ts.
    captureDevToolsFlag(window.location.search);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => captureDevToolsFlag(event.urlAfterRedirects || event.url));
  }

  /** Set the document's <html lang> to the active language's base subtag. */
  private syncDocumentLanguage(language: string): void {
    const base = (language || '').split('-')[0];
    if (base) {
      document.documentElement.lang = base;
    }
  }

  /**
   * Global UI click sound: one delegated listener on press. Plays only for
   * actionable elements (see clickSoundTarget), and the AudioContext resumes
   * inside this user gesture. `pointerdown` makes it feel snappy/tactile.
   */
  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (clickSoundTarget(event.target)) {
      this.clickSound.play();
    }
  }
}
